import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  ActivityLogLevel,
  ITaskActivitySummary,
  ITaskWithMerchant,
} from "../interfaces";
import { useApi } from "../hooks";

interface ITaskState {
  task: ITaskWithMerchant | null;
  activities: ITaskActivitySummary[];
  loading: boolean;
  activitiesLoading: boolean;
  minLogLevel: ActivityLogLevel;
  setMinLogLevel: (level: ActivityLogLevel) => void;
  autoRefresh: boolean;
  setAutoRefresh: (enabled: boolean) => void;
}

const initialState: ITaskState = {
  task: null,
  activities: [],
  loading: true,
  activitiesLoading: true,
  minLogLevel: ActivityLogLevel.DEBUG,
  setMinLogLevel: () => {},
  autoRefresh: false,
  setAutoRefresh: () => {},
};

const TaskContext = createContext<ITaskState>(initialState);

export const useTask = () => useContext(TaskContext);

interface Props {
  taskId?: string;
}

export const TaskProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  taskId,
}) => {
  const { get } = useApi();
  const [minLogLevel, setMinLogLevel] = useState<ActivityLogLevel>(
    ActivityLogLevel.DEBUG
  );
  const [task, setTask] = useState<ITaskWithMerchant | null>(null);
  const [activities, setActivities] = useState<ITaskActivitySummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activitiesLoading, setActivitiesLoading] = useState<boolean>(true);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);

  useEffect(() => {
    const fetchTask = async (showLoading: boolean = false) => {
      if (showLoading) setLoading(true);
      try {
        const taskResponse = await get<ITaskWithMerchant>(`/tasks/${taskId}`);
        if (taskResponse) setTask(taskResponse);
      } catch (error) {
        console.error("Failed to fetch task:", error);
      } finally {
        if (showLoading) setLoading(false);
      }
    };

    const fetchActivities = async () => {
      setActivitiesLoading(true);
      try {
        const activitiesResponse = await get<ITaskActivitySummary[]>(
          `/tasks/${taskId}/activities`
        );
        if (activitiesResponse) setActivities(activitiesResponse);
      } catch (error) {
        console.error("Failed to fetch activities:", error);
      } finally {
        setActivitiesLoading(false);
      }
    };

    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      const isFirst = isInitialLoadRef.current;
      isInitialLoadRef.current = false;

      // Task loads first (fast), activities follow in parallel
      fetchTask(isFirst);
      fetchActivities();

      if (autoRefresh) {
        pollingIntervalRef.current = setInterval(() => {
          fetchTask(false);
          fetchActivities();
        }, 5000);
      }
    };

    if (taskId) {
      startPolling();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [taskId, autoRefresh]);

  return (
    <TaskContext.Provider
      value={{
        task,
        activities,
        loading,
        activitiesLoading,
        minLogLevel,
        setMinLogLevel,
        autoRefresh,
        setAutoRefresh,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};
